import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { timer } from 'rxjs';

import { Grid } from '../../shared/components/grid/grid';
import { GridPage } from '../../shared/components/grid/grid-page';
import { GridCellDirective } from '../../shared/components/grid/directives/grid-cell.directive';
import {
  GridColumn,
  GridRowAction,
  GridRowActionEvent,
  GridTab,
  gridColumns,
} from '../../shared/components/grid/models/grid.model';
import { Badge } from '../../shared/components/badge/badge';
import { Toggle } from '../../shared/components/forms/toggle';
import { ModalService } from '../../shared/components/modal/modal.service';
import { ToastService } from '../../shared/services/toast-service';
import { relativeTime } from '../../shared/utils/relative-time';
import { ContextService } from '../../core/context.service';
import { Role } from '../../core/auth/models/role';
import { LanguageService } from '../../core/i18n/language.service';
import { AlertService } from './services/alert.service';
import { AlertRuleService } from './services/alert-rule.service';
import {
  Alert,
  AlertDeliveryStatus,
  AlertRule,
  AlertRuleOptions,
  isDeliveryFailing,
  operatorSymbol,
  optionLabel,
} from './models/alert.model';
import { RuleDialogComponent } from './rule-dialog/rule-dialog.component';

type AlertsTab = 'active' | 'history' | 'rules' | 'delivery';

/**
 * Alerts page (/:org/alerts) — the table-first layout from the alerts design
 * (option 1a): Active / History / Rules tabs over the universal grid.
 *
 * Threshold alerts land within ~1 heartbeat (≤60 s) and offline alerts within
 * threshold + ≤2 min, so the whole view refetches once a minute. Viewers get a
 * read-only page; rule CRUD (builder dialog, enable toggle, delete) is
 * tenant-admin only.
 */
const POLL_INTERVAL_MS = 60_000;
/** Page size for the FIRING snapshot that feeds the tab badge + per-rule counts. */
const FIRING_SNAPSHOT_SIZE = 100;

@Component({
  selector: 'app-alerts',
  standalone: true,
  imports: [
    FormsModule,
    TranslocoDirective,
    Grid,
    GridPage,
    GridCellDirective,
    Badge,
    Toggle,
    RuleDialogComponent,
  ],
  templateUrl: './alerts.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlertsComponent implements OnInit {
  private readonly alertService = inject(AlertService);
  private readonly ruleService = inject(AlertRuleService);
  private readonly contextService = inject(ContextService);
  private readonly toastService = inject(ToastService);
  private readonly modal = inject(ModalService);
  private readonly transloco = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly activeTab = signal<AlertsTab>('active');

  // Alerts grid (Active + History tabs) — server-side pagination.
  readonly alerts = signal<Alert[]>([]);
  readonly page = signal(0);
  readonly size = signal(20);
  readonly totalElements = signal(0);
  readonly totalPages = signal(0);

  // Rules grid — the list endpoint is unpaginated (name-ordered), so paging
  // is client-side over the full array.
  readonly rules = signal<AlertRule[]>([]);
  readonly rulesPage = signal(0);
  readonly rulesSize = signal(20);

  readonly options = signal<AlertRuleOptions | null>(null);

  // Delivery tab — per-channel notification health. Tiny (one row per
  // channel), unpaginated; also drives the red dot on the tab label.
  readonly deliveryStatuses = signal<AlertDeliveryStatus[]>([]);
  readonly deliveryFailing = computed(() => this.deliveryStatuses().some(isDeliveryFailing));

  // FIRING snapshot, independent of the visible tab: feeds the Active tab
  // badge (exact totalElements) and the per-rule open-alert counts (capped at
  // the snapshot size — fine in practice, the badge stays exact).
  readonly firingTotal = signal<number | null>(null);
  private readonly firingAlerts = signal<Alert[]>([]);

  readonly isRuleDialogOpen = signal(false);
  readonly editingRule = signal<AlertRule | null>(null);

  // Per-grid loading, user-initiated fetches only — the 60 s poll passes
  // silent=true so a background refresh never dims the visible table.
  readonly isAlertsLoading = signal(false);
  readonly isRulesLoading = signal(false);
  readonly isDeliveryLoading = signal(false);

  private readonly roles = toSignal(this.contextService.roles$, { initialValue: [] as string[] });
  readonly isAdmin = computed(() => {
    const roles = this.roles();
    return roles.includes(Role.TENANT_ADMIN) || roles.includes(Role.SUPER_ADMIN);
  });

  /** Stable row ids for selection/tracking. */
  readonly alertId = (a: Alert) => String(a.id);
  readonly ruleId = (r: AlertRule) => String(r.id);
  /** Narrow the erased-generic rows projected grid templates receive. */
  readonly asRule = (row: unknown): AlertRule => row as AlertRule;
  readonly asDelivery = (row: unknown): AlertDeliveryStatus => row as AlertDeliveryStatus;

  /** One row per channel — the channel is the natural id. */
  readonly deliveryId = (s: AlertDeliveryStatus) => s.channel;

  private readonly ruleById = computed(() => new Map(this.rules().map((r) => [r.id, r])));

  private readonly firingCountByRule = computed(() => {
    const counts = new Map<string, number>();
    for (const alert of this.firingAlerts()) {
      if (alert.ruleId == null) continue;
      counts.set(alert.ruleId, (counts.get(alert.ruleId) ?? 0) + 1);
    }
    return counts;
  });

  readonly pagedRules = computed(() => {
    const start = this.rulesPage() * this.rulesSize();
    return this.rules().slice(start, start + this.rulesSize());
  });
  readonly rulesTotalPages = computed(() =>
    Math.max(1, Math.ceil(this.rules().length / this.rulesSize())),
  );

  readonly tabs = computed<GridTab[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    const firing = this.firingTotal();
    return [
      {
        id: 'active',
        label: this.transloco.translate('alerts.tabs.active'),
        count: firing ?? undefined,
      },
      { id: 'history', label: this.transloco.translate('alerts.tabs.history') },
      { id: 'rules', label: this.transloco.translate('alerts.tabs.rules') },
      {
        id: 'delivery',
        label: this.transloco.translate('alerts.tabs.delivery'),
        dot: this.deliveryFailing(),
      },
    ];
  });

  readonly alertColumns = computed<GridColumn<Alert>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    const t = (k: string) => this.transloco.translate(`alerts.columns.${k}`);
    const firing = this.transloco.translate('alerts.status.firing');
    const resolved = this.transloco.translate('alerts.status.resolved');
    const dash = this.transloco.translate('alerts.dash');
    const columns = gridColumns<Alert>([
      {
        key: 'status',
        label: t('status'),
        type: 'status',
        width: 120,
        alwaysVisible: true,
        status: (value) =>
          value === 'FIRING' ? { label: firing, color: 'red' } : { label: resolved, color: 'green' },
      },
      { key: 'detail', label: t('alert'), type: 'text', width: 300, alwaysVisible: true },
      { key: 'subject', label: t('device'), type: 'text', textVariant: 'strong', width: 160 },
      {
        key: 'rule',
        label: t('rule'),
        type: 'text',
        width: 180,
        // Client-side join against the rule list; the rule may have been
        // deleted since the alert fired.
        value: (row) =>
          (row.ruleId != null ? this.ruleById().get(row.ruleId)?.name : null) ??
          this.transloco.translate('alerts.deletedRule'),
      },
      {
        key: 'startedAt',
        label: t('started'),
        type: 'text',
        textVariant: 'muted',
        width: 130,
        value: (row) => relativeTime(row.startedAt),
      },
    ]);
    if (this.activeTab() === 'history') {
      columns.push({
        key: 'resolvedAt',
        label: t('resolved'),
        type: 'text',
        textVariant: 'muted',
        width: 130,
        value: (row) => (row.resolvedAt ? relativeTime(row.resolvedAt) : dash),
      });
    }
    return columns;
  });

  readonly alertRowActions = computed<GridRowAction<Alert>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    return [
      {
        id: 'openDevice',
        label: this.transloco.translate('alerts.openDevice'),
        icon: 'icon-arrow-right',
        placement: 'inline',
      },
    ];
  });

  readonly ruleColumns = computed<GridColumn<AlertRule>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    const t = (k: string) => this.transloco.translate(`alerts.rules.columns.${k}`);
    return gridColumns<AlertRule>([
      { key: 'enabled', label: t('enabled'), type: 'template', template: 'enabled', width: 90 },
      {
        key: 'name',
        label: t('name'),
        type: 'text',
        textVariant: 'strong',
        width: 200,
        alwaysVisible: true,
      },
      {
        key: 'condition',
        label: t('condition'),
        type: 'text',
        width: 260,
        value: (row) => this.conditionText(row),
      },
      { key: 'openAlerts', label: t('openAlerts'), type: 'template', template: 'openAlerts', width: 140 },
      { key: 'lastUpdated', label: t('lastUpdated'), type: 'date', width: 150, hideOnCompact: true },
    ]);
  });

  readonly deliveryColumns = computed<GridColumn<AlertDeliveryStatus>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    const t = (k: string) => this.transloco.translate(`alerts.delivery.columns.${k}`);
    const delivering = this.transloco.translate('alerts.delivery.status.delivering');
    const failing = this.transloco.translate('alerts.delivery.status.failing');
    return gridColumns<AlertDeliveryStatus>([
      {
        key: 'status',
        label: t('status'),
        type: 'status',
        width: 120,
        alwaysVisible: true,
        // Derived, not a payload field: failing while an error is outstanding.
        value: (row) => (isDeliveryFailing(row) ? 'FAILING' : 'DELIVERING'),
        status: (value) =>
          value === 'FAILING' ? { label: failing, color: 'red' } : { label: delivering, color: 'green' },
      },
      {
        key: 'channel',
        label: t('channel'),
        type: 'text',
        textVariant: 'strong',
        width: 160,
        alwaysVisible: true,
        value: (row) => optionLabel((k) => this.transloco.translate(k), 'channels', row.channel),
      },
      { key: 'lastError', label: t('lastError'), type: 'template', template: 'lastError', width: 320 },
      { key: 'lastErrorAt', label: t('lastErrorAt'), type: 'date', width: 160 },
      { key: 'lastSuccessAt', label: t('lastSuccess'), type: 'date', width: 160 },
    ]);
  });

  readonly ruleRowActions = computed<GridRowAction<AlertRule>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    if (!this.isAdmin()) return [];
    return [
      {
        id: 'edit',
        label: this.transloco.translate('alerts.rules.edit'),
        icon: 'icon-edit-pencil',
        placement: 'inline',
      },
      {
        id: 'delete',
        label: this.transloco.translate('alerts.rules.delete'),
        icon: 'icon-delete-trash',
        placement: 'inline',
        danger: true,
      },
    ];
  });

  ngOnInit(): void {
    this.loadOptions();
    this.loadRules();
    this.loadAlerts();
    this.refreshFiringSnapshot();
    this.loadDeliveryStatuses();

    // Threshold alerts appear within one heartbeat, offline alerts within
    // threshold + 2 min — a 60 s refetch keeps every tab honest.
    timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.activeTab() === 'active' || this.activeTab() === 'history') {
          this.loadAlerts(true);
        }
        this.loadRules(true);
        this.refreshFiringSnapshot();
        // Always, not just on the Delivery tab — it keeps the red dot honest.
        this.loadDeliveryStatuses(true);
      });
  }

  onTabChange(id: string): void {
    this.activeTab.set(id as AlertsTab);
    if (id === 'rules') {
      this.rulesPage.set(0);
      this.loadRules();
    } else if (id === 'delivery') {
      this.loadDeliveryStatuses();
    } else {
      this.page.set(0);
      this.loadAlerts();
    }
  }

  onPageChange(page: number): void {
    this.page.set(page);
    this.loadAlerts();
  }

  onPageSizeChange(size: number): void {
    this.size.set(size);
    this.page.set(0);
    this.loadAlerts();
  }

  onAlertAction(event: GridRowActionEvent): void {
    const alert = event.row as Alert;
    if (event.actionId === 'openDevice') {
      const org = this.contextService.getOrganization()?.name;
      if (org) {
        void this.router.navigate(['/', org, 'devices', alert.subject]);
      }
    }
  }

  onRuleAction(event: GridRowActionEvent): void {
    const rule = event.row as AlertRule;
    if (event.actionId === 'edit') {
      this.openEditRule(rule);
    } else if (event.actionId === 'delete') {
      void this.deleteRule(rule);
    }
  }

  openAddRule(): void {
    if (!this.options()) return; // vocabulary not loaded — nothing to build with
    this.editingRule.set(null);
    this.isRuleDialogOpen.set(true);
  }

  openEditRule(rule: AlertRule): void {
    if (!this.options()) return;
    this.editingRule.set(rule);
    this.isRuleDialogOpen.set(true);
  }

  closeRuleDialog(): void {
    this.isRuleDialogOpen.set(false);
    this.editingRule.set(null);
  }

  onRuleSaved(): void {
    this.loadRules();
    this.refreshFiringSnapshot();
    if (this.activeTab() !== 'rules') {
      this.loadAlerts();
    }
  }

  onToggleEnabled(rule: AlertRule, enabled: boolean): void {
    if (!this.isAdmin() || rule.enabled === enabled) return;
    // Optimistic flip; revert on failure.
    this.rules.update((rules) => rules.map((r) => (r.id === rule.id ? { ...r, enabled } : r)));
    this.ruleService
      .updateRule(rule.id, {
        name: rule.name,
        metric: rule.metric,
        operator: rule.operator,
        threshold: rule.threshold,
        enabled,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => {
          this.rules.update((rules) =>
            rules.map((r) => (r.id === rule.id ? { ...r, enabled: rule.enabled } : r)),
          );
          this.toastService.show(this.transloco.translate('alerts.rules.toast.saveFailed'), 'error');
        },
      });
  }

  onRulesPageChange(page: number): void {
    this.rulesPage.set(page);
  }

  onRulesPageSizeChange(size: number): void {
    this.rulesSize.set(size);
    this.rulesPage.set(0);
  }

  /** Open-alert count for a rule, from the FIRING snapshot. */
  firingCountFor(ruleId: string): number {
    return this.firingCountByRule().get(ruleId) ?? 0;
  }

  private async deleteRule(rule: AlertRule): Promise<void> {
    const confirmed = await this.modal.confirm({
      kind: 'destructive',
      destructive: true,
      title: this.transloco.translate('alerts.rules.deleteTitle'),
      message: this.transloco.translate('alerts.rules.deleteMessage', { name: rule.name }),
      confirmLabel: this.transloco.translate('common.delete'),
    });
    if (!confirmed) return;

    this.ruleService
      .deleteRule(rule.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show(this.transloco.translate('alerts.rules.toast.deleted'), 'success');
          // Deleting a rule also resolves its open alerts server-side.
          this.loadRules();
          this.refreshFiringSnapshot();
          if (this.activeTab() !== 'rules') {
            this.loadAlerts();
          }
        },
        error: () =>
          this.toastService.show(this.transloco.translate('alerts.rules.toast.deleteFailed'), 'error'),
      });
  }

  private loadAlerts(silent = false): void {
    const status = this.activeTab() === 'active' ? 'FIRING' : null;
    if (!silent) this.isAlertsLoading.set(true);
    this.alertService
      .getAlerts(status, this.page(), this.size())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.alerts.set(response.data);
          this.page.set(response.page);
          this.size.set(response.size);
          this.totalElements.set(response.totalElements);
          this.totalPages.set(response.totalPages);
          if (status === 'FIRING') {
            this.firingTotal.set(response.totalElements);
          }
          this.isAlertsLoading.set(false);
        },
        error: () => {
          this.toastService.show(this.transloco.translate('alerts.toast.loadFailed'), 'error');
          this.isAlertsLoading.set(false);
        },
      });
  }

  private loadRules(silent = false): void {
    if (!silent) this.isRulesLoading.set(true);
    this.ruleService
      .getRules()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.rules.set(response.data ?? []);
          this.isRulesLoading.set(false);
        },
        error: () => {
          this.toastService.show(this.transloco.translate('alerts.rules.toast.loadFailed'), 'error');
          this.isRulesLoading.set(false);
        },
      });
  }

  private loadOptions(): void {
    this.ruleService
      .getOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => this.options.set(response.data),
        error: () =>
          this.toastService.show(this.transloco.translate('alerts.rules.toast.loadFailed'), 'error'),
      });
  }

  private loadDeliveryStatuses(silent = false): void {
    if (!silent) this.isDeliveryLoading.set(true);
    this.alertService
      .getDeliveryStatuses()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.deliveryStatuses.set(response.data);
          this.isDeliveryLoading.set(false);
        },
        // Silent: the tab and dot just go stale until the next poll tick.
        error: () => this.isDeliveryLoading.set(false),
      });
  }

  private refreshFiringSnapshot(): void {
    this.alertService
      .getAlerts('FIRING', 0, FIRING_SNAPSHOT_SIZE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.firingAlerts.set(response.data);
          this.firingTotal.set(response.totalElements);
        },
        // Silent: the badge/counts just go stale until the next poll tick.
        error: () => undefined,
      });
  }

  /** Compact condition summary, e.g. "Battery capacity < 20 %". */
  private conditionText(rule: AlertRule): string {
    // OFFLINE (and any future condition-less metric) has no operator/threshold
    // — the window is derived per device from its power profile.
    if (rule.operator == null || rule.threshold == null) {
      return this.transloco.translate('alerts.rules.offlineCondition');
    }
    const metric = this.options()?.metrics.find((m) => m.value === rule.metric);
    const label = optionLabel((k) => this.transloco.translate(k), 'metrics', rule.metric);
    const unit = metric?.unit ? ` ${metric.unit}` : '';
    return `${label} ${operatorSymbol(rule.operator)} ${rule.threshold}${unit}`;
  }
}
